import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Upload, Select, message, Image, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import apiClient from '../services/apiConfig';

interface ExternalDiscount {
  id: number;
  business_name: string;
  description: string;
  logo: string | null;
  logo_url?: string;
  category: string;
  discount_classic: number;
  discount_premium: number;
  discount_silver: number;
  address: string;
  phone: string;
  website: string;
  is_active: boolean;
}

const categories = [
  { value: 'food', label: 'Gastronomía' },
  { value: 'entertainment', label: 'Entretenimiento' },
  { value: 'beach', label: 'Playa' },
  { value: 'shopping', label: 'Compras' },
  { value: 'activities', label: 'Actividades' },
  { value: 'other', label: 'Otro' },
];

const ExternalDiscountsPage: React.FC = () => {
  const [discounts, setDiscounts] = useState<ExternalDiscount[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<ExternalDiscount | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const response = await apiClient.get('/discounts');
      if (response.data.success) {
        setDiscounts(response.data.data);
      }
    } catch (error) {
      message.error('Error al cargar descuentos');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedDiscount(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (discount: ExternalDiscount) => {
    setSelectedDiscount(discount);
    form.setFieldsValue({
      business_name: discount.business_name,
      description: discount.description,
      category: discount.category,
      address: discount.address,
      phone: discount.phone,
      website: discount.website,
      discount_classic: discount.discount_classic,
      discount_premium: discount.discount_premium,
      discount_silver: discount.discount_silver,
      is_active: discount.is_active,
      logo: discount.logo ? [{
        uid: '-1',
        name: discount.logo,
        status: 'done',
        url: discount.logo_url || discount.logo
      }] : []
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '¿Eliminar este descuento?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción no se puede deshacer',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      async onOk() {
        try {
          await apiClient.delete(`/discounts/${id}`);
          message.success('Descuento eliminado');
          fetchDiscounts();
        } catch (error) {
          message.error('Error al eliminar descuento');
        }
      }
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('business_name', values.business_name);
      formData.append('description', values.description || '');
      formData.append('category', values.category);
      formData.append('address', values.address || '');
      formData.append('phone', values.phone || '');
      formData.append('website', values.website || '');
      formData.append('discount_classic', values.discount_classic.toString());
      formData.append('discount_premium', values.discount_premium.toString());
      formData.append('discount_silver', values.discount_silver.toString());
      formData.append('is_active', values.is_active ? '1' : '0');

      if (values.logo && values.logo[0]?.originFileObj) {
        formData.append('logo', values.logo[0].originFileObj);
      }

      if (selectedDiscount) {
        await apiClient.post(`/discounts/${selectedDiscount.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Descuento actualizado');
      } else {
        await apiClient.post('/discounts', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Descuento creado');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchDiscounts();
    } catch (error) {
      message.error('Error al guardar descuento');
    }
  };

  const getCategoryLabel = (value: string) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  const columns = [
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      width: 80,
      render: (_: string | null, record: ExternalDiscount) => record.logo_url ? (
        <Image src={record.logo_url} alt="logo" width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} />
      ) : <div style={{ width: 50, height: 50, background: '#f0f0f0', borderRadius: 4 }} />
    },
    {
      title: 'Comercio',
      dataIndex: 'business_name',
      key: 'business_name',
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag>{getCategoryLabel(category)}</Tag>
    },
    {
      title: 'Classic',
      dataIndex: 'discount_classic',
      key: 'discount_classic',
      render: (discount: number) => <Tag color="blue">{discount}%</Tag>
    },
    {
      title: 'Premium',
      dataIndex: 'discount_premium',
      key: 'discount_premium',
      render: (discount: number) => <Tag color="purple">{discount}%</Tag>
    },
    {
      title: 'Silver',
      dataIndex: 'discount_silver',
      key: 'discount_silver',
      render: (discount: number) => <Tag color="gold">{discount}%</Tag>
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Activo' : 'Inactivo'}</Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: ExternalDiscount) => (
        <>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Editar</Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Eliminar</Button>
        </>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Descuentos Externos</h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>Gestiona descuentos en comercios de San Bernardo</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nuevo Descuento
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={discounts}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={selectedDiscount ? 'Editar Descuento' : 'Crear Descuento'}
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ is_active: true, category: 'food' }}>
          <Form.Item name="business_name" label="Nombre del comercio" rules={[{ required: true, message: 'Ingresa el nombre' }]}>
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="category" label="Categoría" rules={[{ required: true }]}>
            <Select options={categories} />
          </Form.Item>

          <Form.Item name="address" label="Dirección">
            <Input />
          </Form.Item>

          <Form.Item name="phone" label="Teléfono">
            <Input />
          </Form.Item>

          <Form.Item name="website" label="Sitio web">
            <Input placeholder="https://..." />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12 }}>Descuentos por categoría (%)</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Form.Item name="discount_classic" label="Classic" rules={[{ required: true }]}>
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="discount_premium" label="Premium" rules={[{ required: true }]}>
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="discount_silver" label="Silver" rules={[{ required: true }]}>
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </div>

          <Form.Item name="logo" label="Logo" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
            <Upload listType="picture" maxCount={1} beforeUpload={() => false}>
              <Button icon={<UploadOutlined />}>Seleccionar logo</Button>
            </Upload>
          </Form.Item>

          <Form.Item name="is_active" label="Activo" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {selectedDiscount ? 'Actualizar' : 'Crear'}
            </Button>
            <Button onClick={() => { setIsModalVisible(false); form.resetFields(); }}>
              Cancelar
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ExternalDiscountsPage;
